import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  switchMap,
  take,
  timeout,
} from 'rxjs/operators';

import { AuthenticationService } from '@/app/core/authentication/authentication.service';
import { LocaleService } from '@/app/core/services/locale.service';
import { environment } from '@/environments/environment';

type TranslationMap = Record<string, string>;

/**
 * Loads Rouse Product Type translations from Firestore (via the
 * /localization/rouse-product-types endpoint) and exposes a synchronous
 * `translate` method for grid cell rendering.
 *
 * The Postgres data service returns Rouse Product Type values in raw English
 * by design (see analytics-rdo-dataservice fmrr_translated_dim_rouse_product_types
 * — translations deferred to Firestore). This service is the client-side half
 * of that contract.
 */
@Injectable({
  providedIn: 'root',
})
export class RouseProductTypeTranslationService {
  public readonly $serviceIsReady = new BehaviorSubject<boolean>(false);

  private translations: TranslationMap = {};
  private loadingLocale: string | null = null;
  private translationSub: Subscription | null = null;

  constructor(
    private http: HttpClient,
    private authenticationService: AuthenticationService,
    private localeService: LocaleService
  ) {
    if (this.localeService.localeWasFoundOnInit()) {
      this.loadTranslations(this.localeService.getLocale());
    }

    this.localeService.observableLocale
      .pipe(
        filter((locale) => !!locale),
        distinctUntilChanged()
      )
      .subscribe((locale) => {
        this.loadTranslations(locale);
      });
  }

  /**
   * Translate a raw Rouse Product Type name (e.g. "100000-114999 Lb Excavator")
   * to the current locale. Falls back to the raw value if no translation exists
   * — keeps the cell readable rather than showing an empty string.
   */
  public translate(value: string | null | undefined): string {
    if (!value) {
      return value as string;
    }
    return this.translations[value] ?? value;
  }

  public waitUntilReady(): Observable<boolean> {
    return new Observable<boolean>((obs) => {
      if (this.$serviceIsReady.value) {
        obs.next(true);
        obs.complete();
        return;
      }
      const sub = this.$serviceIsReady.subscribe((ready) => {
        if (ready) {
          obs.next(true);
          obs.complete();
        }
      });
      return () => sub.unsubscribe();
    });
  }

  private loadTranslations(locale: string): void {
    if (locale === this.loadingLocale) {
      return;
    }
    this.loadingLocale = locale;
    this.translationSub?.unsubscribe();
    this.$serviceIsReady.next(false);

    let url = environment.apiUrl;
    url = url.endsWith('/') ? url : `${url}/`;
    const endpoint = `${url}localization/rouse-product-types?locale=${locale}`;

    this.translationSub = this.authenticationService.Authenticated.pipe(
      filter((v) => v),
      take(1),
      switchMap(() => this.authenticationService.getAuthToken()),
      switchMap((token: any) => {
        const headers = new HttpHeaders().set(
          'Authorization',
          `Bearer ${token.AccessToken}`
        );
        return this.http.get<TranslationMap>(endpoint, {
          headers,
          withCredentials: true,
        });
      }),
      timeout(15000),
      catchError((err) => {
        console.error(
          '[RouseProductTypeTranslation] Failed to load translations:',
          err
        );
        return of({} as TranslationMap);
      })
    ).subscribe((translations) => {
      this.translations = translations || {};
      this.$serviceIsReady.next(true);
    });
  }
}
